import type { Driver } from 'driver.js';
import { TOUR_STEP_IDS } from './config';
import type { StageSize, TourStep } from './types';

export const getStepElementSelector = (step?: TourStep | null) => {
  if (!step) return;
  if (step.selector) return step.selector;
  if (step.id) return `[data-tour-id="${step.id}"]`;
};

export const getStepElement = (step?: TourStep | null) => {
  if (!step) return null;

  const stepSelector = getStepElementSelector(step);
  if (!stepSelector) return null;

  const element = document.querySelector(stepSelector) as HTMLElement | null;
  if (!element) return null;

  const isElementVisible = element.checkVisibility({
    opacityProperty: true,
    visibilityProperty: true,
  });

  if (!isElementVisible) return null;

  return element;
};

export const showStage = () => {
  const overlay = document.querySelector('.driver-overlay') as HTMLElement;
  const popover = document.querySelector('.driver-popover') as HTMLElement;

  if (overlay && popover) {
    overlay.style.visibility = 'visible';
    popover.style.visibility = 'visible';
  }

  const fullOverlay = document.querySelector('.driver-full-overlay') as HTMLElement;

  if (fullOverlay) {
    fullOverlay.style.visibility = 'hidden';
  }
};

export const hideStage = () => {
  const fullOverlay = (document.querySelector('.driver-full-overlay') || document.createElement('div')) as HTMLElement;

  if (!fullOverlay.classList.contains('driver-full-overlay')) {
    fullOverlay.classList.add('driver-full-overlay');
  }

  fullOverlay.style.visibility = 'visible';

  if (!document.body.contains(fullOverlay)) {
    document.body.appendChild(fullOverlay);
  }

  const overlay = document.querySelector('.driver-overlay') as HTMLElement;
  const popover = document.querySelector('.driver-popover') as HTMLElement;

  if (overlay && popover) {
    overlay.style.visibility = 'hidden';
    popover.style.visibility = 'hidden';
  }
};

export const removeStage = () => {
  const fullOverlay = document.querySelector('.driver-full-overlay');
  if (fullOverlay) {
    document.body.removeChild(fullOverlay);
  }

  const overlay = document.querySelector('.driver-overlay');
  if (overlay) {
    document.body.removeChild(overlay);
  }

  const popover = document.querySelector('.driver-popover');
  if (popover) {
    document.body.removeChild(popover);
  }
};

export const isElementInViewport = (element: HTMLElement) => {
  const filtersDrawer = document.querySelector(`[data-tour-id="${TOUR_STEP_IDS.FILTERS_DRAWER}"]`) as HTMLElement;

  if (!filtersDrawer || !filtersDrawer.contains(element)) {
    return true;
  }

  const rect = element.getBoundingClientRect();
  return rect.top >= 0 && rect.bottom <= window.innerHeight;
};

export const drawStage = (driver: Driver, stageSize: StageSize = {}) => {
  const config = driver.getConfig();

  const element = getStepElement(driver.getActiveStep() as TourStep);

  const stagePath = document.querySelector('.driver-overlay path');

  if (!element || !stagePath) return;

  const stage = element.getBoundingClientRect();

  const windowX = window.innerWidth;
  const windowY = window.innerHeight;

  const stagePadding = config.stagePadding || 0;
  const stageRadius = config.stageRadius || 0;

  const stageWidth = (stageSize.width ?? stage.width) + stagePadding * 2;
  const stageHeight = (stageSize.height ?? stage.height) + stagePadding * 2;

  // prevent glitches when stage is too small for radius
  const limitedRadius = Math.min(stageRadius, stageWidth / 2, stageHeight / 2);

  // no value below 0 allowed + round down
  const normalizedRadius = Math.floor(Math.max(limitedRadius, 0));

  const highlightBoxX = stage.x - stagePadding + normalizedRadius;
  const highlightBoxY = stage.y - stagePadding;
  const highlightBoxWidth = stageWidth - normalizedRadius * 2;
  const highlightBoxHeight = stageHeight - normalizedRadius * 2;

  const pathDefinition = `M${windowX},0L0,0L0,${windowY}L${windowX},${windowY}L${windowX},0Z
    M${highlightBoxX},${highlightBoxY} h${highlightBoxWidth} a${normalizedRadius},${normalizedRadius} 0 0 1 ${normalizedRadius},${normalizedRadius} v${highlightBoxHeight} a${normalizedRadius},${normalizedRadius} 0 0 1 -${normalizedRadius},${normalizedRadius} h-${highlightBoxWidth} a${normalizedRadius},${normalizedRadius} 0 0 1 -${normalizedRadius},-${normalizedRadius} v-${highlightBoxHeight} a${normalizedRadius},${normalizedRadius} 0 0 1 ${normalizedRadius},-${normalizedRadius} z`;

  stagePath.setAttribute('d', pathDefinition);

  showStage();
};
